import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, MessageCircle, Phone } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import api from "@/lib/api";
import { telLink } from "@/lib/utils-format";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import SigniaBadge from "@/components/SigniaBadge";
import GlobalSearch from "@/components/GlobalSearch";

const NAV = [
    { to: "/products", label: "Products" },
    { to: "/compare", label: "Compare" },
    { to: "/learn", label: "Learn" },
    { to: "/upgrade", label: "Upgrade" },
    { to: "/book-trial", label: "Book Trial" },
    { to: "/clinics", label: "Clinics" },
];

export default function Header() {
    const { count } = useCart();
    const [open, setOpen] = useState(false);
    const [settings, setSettings] = useState({ whatsapp_number: "919971925446", call_number: "+919888438843" });
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    }, []);

    return (
        <header className="glass sticky top-0 z-50" data-testid="site-header">
            <div className="container-x flex items-center justify-between h-[68px] md:h-[76px]">
                <Link to="/" className="flex items-center gap-4" data-testid="logo-link">
                    <div className="flex flex-col leading-none">
                        <span className="font-serif text-[22px] md:text-[26px] tracking-tight text-[#1a1a1a]">
                            signiastyletto<span className="text-[#003E4D]">ix</span>
                        </span>
                        <span className="eyebrow text-[10px] mt-1">Powered by HearClear</span>
                    </div>
                    <span className="hidden md:block w-px h-9 bg-black/10" />
                    <SigniaBadge variant="compact" className="hidden md:flex" />
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    <GlobalSearch />
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `px-4 py-2 text-[14px] font-medium rounded-full transition ${
                                    isActive
                                        ? "text-[#003E4D] bg-[#003E4D]/[0.06]"
                                        : "text-[#1a1a1a] hover:bg-black/5"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <a
                        href={telLink(settings.call_number)}
                        className="hidden md:inline-flex btn-ghost items-center gap-2"
                        data-testid="header-call-cta"
                    >
                        <Phone size={16} /> Call
                    </a>
                    <WhatsAppCTA
                        waNumber={settings.whatsapp_number}
                        message="Hi, I want the best price for Signia Styletto IX."
                        sourcePage="header"
                        className="hidden md:inline-flex btn-whatsapp text-[13px] !py-2.5 !px-5"
                        data-testid="header-whatsapp-cta"
                    >
                        <MessageCircle size={16} /> Get Best Price
                    </WhatsAppCTA>
                    <button
                        onClick={() => navigate("/cart")}
                        className="relative w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/5 transition"
                        data-testid="header-cart-btn"
                        aria-label="Cart"
                    >
                        <ShoppingBag size={18} />
                        {count > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-[#003E4D] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                                {count}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setOpen(true)}
                        className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/5 transition"
                        data-testid="mobile-menu-btn"
                        aria-label="Open menu"
                    >
                        <Menu size={20} />
                    </button>
                </div>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
                    <div
                        className="absolute right-0 top-0 h-full w-[88%] max-w-[360px] bg-white p-6 shadow-2xl flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="mobile-menu"
                    >
                        <div className="flex items-center justify-between mb-8">
                            <span className="font-serif text-[22px]">
                                signiastyletto<span className="text-[#003E4D]">ix</span>
                            </span>
                            <button
                                onClick={() => setOpen(false)}
                                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/5"
                                data-testid="mobile-menu-close"
                                aria-label="Close menu"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="mb-6">
                            <SigniaBadge variant="ribbon" />
                        </div>
                        <nav className="flex flex-col gap-1">
                            {NAV.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    onClick={() => setOpen(false)}
                                    className="px-4 py-3 text-[15px] font-medium hover:bg-black/5 rounded-lg"
                                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {n.label}
                                </NavLink>
                            ))}
                        </nav>
                        <div className="mt-auto pt-6 flex flex-col gap-3">
                            <a
                                href={waLink(settings.whatsapp_number, "Hi, I want the best price for Signia Styletto IX.")}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-whatsapp justify-center"
                                data-testid="mobile-whatsapp-cta"
                            >
                                <MessageCircle size={16} /> Get Best Price on WhatsApp
                            </a>
                            <a
                                href={telLink(settings.call_number)}
                                className="btn-secondary justify-center"
                                data-testid="mobile-call-cta"
                            >
                                <Phone size={16} /> Call {settings.call_number}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
